/**
 * Status Transition Service
 * Centralized logic for booking/job status transitions
 * Defines allowed transitions, dialog requirements, and validation
 */

// All possible statuses in the unified system
export type BookingStatus =
  | 'UNVERIFIED'
  | 'PENDING_CONFIRMATION'
  | 'CONFIRMED'
  | 'AWAITING_DROPOFF'
  | 'DROPOFF_COMPLETE'
  | 'CANCELLED'
  | 'NO_SHOW';

// Dialog types for different transitions
export type TransitionDialogType = 'none' | 'confirm' | 'notes' | 'cancel' | 'complete' | 'bay_select' | 'quote';

/** Checklist item for transition dialogs */
export interface TransitionChecklistItem {
  id: string;
  label: string;
  description?: string;
  required: boolean;
}

// Transition configuration
export interface TransitionConfig {
  /** Whether this transition is allowed */
  allowed: boolean;
  /** Type of dialog to show */
  dialogType: TransitionDialogType;
  /** Whether notes are required */
  notesRequired: boolean;
  /** Whether public note is allowed */
  allowPublicNote: boolean;
  /** Whether bay selection is required */
  requiresBaySelection: boolean;
  /** Whether quote/estimate with line items is required */
  requiresQuote: boolean;
  /** Confirmation message */
  confirmMessage?: string;
  /** Dialog title */
  dialogTitle?: string;
  /** Checklist items to verify before completing transition */
  checklistItems?: TransitionChecklistItem[];
}

/**
 * Service workflow configuration
 * Per-service customization of which statuses to skip/require
 */
export interface ServiceWorkflowConfig {
  /** Statuses to skip during workflow transitions */
  skipStatuses?: BookingStatus[];
  /** Required statuses by booking type */
  requiredStatuses?: {
    status: BookingStatus;
    bookingTypes?: ('private' | 'insurance' | 'warranty')[];
  }[];
  /** Auto-create estimate from basePrice */
  autoCreateEstimate?: boolean;
}


/**
 * Estimate line item categories
 */
export const ESTIMATE_LINE_ITEM_CATEGORIES = [
  { value: 'labor', label: 'Mano de obra' },
  { value: 'parts', label: 'Recambios' },
  { value: 'materials', label: 'Materiales' },
  { value: 'other', label: 'Otros' },
] as const;

/**
 * Bay types (physical work areas)
 * Unified with service.bayType - services now directly specify their bay type
 */
export const BAY_TYPES = ['mechanical', 'bodywork', 'diagnostics'] as const;
export type BayType = (typeof BAY_TYPES)[number];

export const BAY_TYPE_LABELS: Record<BayType, string> = {
  mechanical: 'Mecánica',
  bodywork: 'Carrocería',
  diagnostics: 'Diagnóstico',
};

/**
 * Cancellation reasons
 */
export const CANCELLATION_REASONS = [
  { value: 'customer_request', label: 'Solicitud del cliente' },
  { value: 'schedule_conflict', label: 'Conflicto de horario' },
  { value: 'vehicle_unavailable', label: 'Vehículo no disponible' },
  { value: 'parts_unavailable', label: 'Piezas no disponibles' },
  { value: 'staff_unavailable', label: 'Personal no disponible' },
  { value: 'duplicate_booking', label: 'Reserva duplicada' },
  { value: 'other', label: 'Otro motivo' },
];

